/**
 * Hook for listening to new lead/contact source events via Socket.IO
 * Server emits 'new-contact-source' when webhook receives new lead
 */

import { useEffect, useRef, useState, useCallback } from 'react'
import { io, Socket } from 'socket.io-client'
import useAuthStore from '@/stores/useAuthStore'
import authSdk from '@/utils/sdk'

// Contact source data from webhook
export interface NewLeadData {
    contact_source_id: number
    contact_source_name: string
    contact_source_email: string
    contact_source_phone: string
    contact_source_company: string
    contact_source_channel: string
    contact_source_status: string
    website_referrer: string
    created_at: string
}

interface UseNewLeadSocketOptions {
    onNewLead?: (data: NewLeadData) => void
    enabled?: boolean
}

// Socket URL - uses same API URL as backend
const getSocketUrl = () => import.meta.env.APP_API_URL || ''
const SOCKET_PATH = '/ws/marketing'
const MAX_RETRIES = 3
const RETRY_DELAY_MS = 3000

export const useNewLeadSocket = (options: UseNewLeadSocketOptions = {}) => {
    const { employee } = useAuthStore()
    const socketRef = useRef<Socket | null>(null)
    const [isConnected, setIsConnected] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const retryCountRef = useRef(0)
    const retryTimeoutRef = useRef<NodeJS.Timeout | null>(null)

    const { onNewLead, enabled = true } = options

    // Store callback in ref to avoid reconnection on callback change
    const onNewLeadRef = useRef(onNewLead)
    onNewLeadRef.current = onNewLead

    const connect = useCallback(() => {
        if (!employee || !enabled || socketRef.current?.connected) return

        const socketUrl = getSocketUrl()
        if (!socketUrl) {
            console.warn('[NewLead Socket] Socket URL not configured')
            return
        }

        const accessToken = authSdk.getAccessToken()
        if (!accessToken) {
            console.error('[NewLead Socket] No access token')
            setError('No access token')
            return
        }

        console.log('[NewLead Socket] Connecting to', socketUrl)

        const socket = io(socketUrl, {
            path: SOCKET_PATH,
            auth: {
                authorization: accessToken,
            },
            reconnection: false,
            timeout: 10000,
            transports: ['websocket', 'polling'],
        })

        socketRef.current = socket

        socket.on('connect', () => {
            console.log('[NewLead Socket] Connected')
            setIsConnected(true)
            setError(null)
            retryCountRef.current = 0
        })

        // Listen for new contact source events
        socket.on('new-contact-source', (data: NewLeadData) => {
            console.log('[NewLead Socket] New lead:', data)
            onNewLeadRef.current?.(data)
        })

        socket.on('connect_error', (err) => {
            console.error('[NewLead Socket] Connection error:', err.message)
            setError(err.message)
            setIsConnected(false)
            handleRetry()
        })

        socket.on('disconnect', (reason) => {
            console.log('[NewLead Socket] Disconnected:', reason)
            setIsConnected(false)
            if (
                reason === 'io server disconnect' ||
                reason === 'transport close'
            ) {
                handleRetry()
            }
        })
    }, [employee, enabled])

    const handleRetry = useCallback(() => {
        if (retryCountRef.current >= MAX_RETRIES) {
            console.log('[NewLead Socket] Max retries reached')
            return
        }

        retryCountRef.current += 1
        const delay = RETRY_DELAY_MS * retryCountRef.current

        console.log(
            `[NewLead Socket] Retry in ${delay}ms (${retryCountRef.current}/${MAX_RETRIES})`
        )

        retryTimeoutRef.current = setTimeout(() => {
            connect()
        }, delay)
    }, [connect])

    const disconnect = useCallback(() => {
        if (retryTimeoutRef.current) {
            clearTimeout(retryTimeoutRef.current)
        }
        if (socketRef.current) {
            socketRef.current.disconnect()
            socketRef.current = null
        }
        setIsConnected(false)
    }, [])

    useEffect(() => {
        if (employee && enabled) {
            connect()
        }
        return () => {
            disconnect()
        }
    }, [employee, enabled, connect, disconnect])

    return {
        isConnected,
        error,
        connect,
        disconnect,
    }
}

export default useNewLeadSocket
